import './index.css'
import { useEffect, useMemo, useState } from 'react'
import { X, CheckCircle2, Search, Pencil, Trash2, FastForward, ChevronDown, ChevronUp } from 'lucide-react'
import type { Card, Transaction } from '../../types'
import { fmt } from '../../lib/format'
import { filterTransactionsBySearch } from '../TransactionList/search'
import { groupInstallments } from '../../lib/installmentGrouping'

const CAT_ABBR: Record<string, string> = {
  'Salário': 'SA', 'Mercado': 'ME', 'Aluguel': 'AL', 'Cartão de crédito': 'CC',
  'Terapia': 'TE', 'Lazer': 'LA', 'Transporte': 'TR', 'Saúde': 'SD',
  'Assinatura': 'AS', 'Esportes': 'ES', 'FIES': 'FI', 'Celular': 'CE',
  'Empréstimo': 'EM', 'Seguro': 'SG', 'Beleza': 'BL', 'Viagem': 'VI',
}

interface FaturaSheetProps {
  open: boolean
  onClose: () => void
  card: Card | null
  transactions: Transaction[]
  onMarkAsPaid?: (cardId: string) => Promise<void>
  onEdit?: (t: Transaction) => void
  onDelete?: (id: string) => void
  onDeleteGroup?: (t: Transaction) => void
  onAmortize?: (t: Transaction) => void
}

export default function FaturaSheet({ open, onClose, card, transactions, onMarkAsPaid, onEdit, onDelete, onDeleteGroup, onAmortize }: FaturaSheetProps) {
  const [marking, setMarking] = useState(false)
  const [query, setQuery] = useState('')
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const cardId = card?.id ?? null
  const items = useMemo(
    () => (cardId ? transactions.filter(t => t.card_id === cardId) : []),
    [cardId, transactions],
  )
  const filteredItems = useMemo(() => filterTransactionsBySearch(items, query), [items, query])
  const total = items.reduce((s, t) => s + t.amount, 0)
  const filteredTotal = filteredItems.reduce((s, t) => s + t.amount, 0)
  const unpaidCount = items.filter(t => !t.realized).length
  const isFiltering = query.trim().length > 0

  useEffect(() => {
    setQuery('')
    setExpandedGroups(new Set())
  }, [cardId])

  function toggleGroup(id: string) {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function renderTransaction(t: Transaction, isChild = false) {
    return (
      <div key={t.id} className="bc-invoice-row" style={isChild ? { paddingLeft: 44, background: 'var(--sand-1)', borderBottom: '1px dashed var(--sand-3)' } : undefined}>
        <div className="bc-invoice-row__icon">
          {CAT_ABBR[t.category] ?? t.category.slice(0, 2).toUpperCase()}
        </div>
        <div className="bc-invoice-row__desc">
          <span className="bc-invoice-row__title">{t.title}</span>
          <span className="bc-invoice-row__meta">
            <span>{t.category}</span>
            {isChild && <span className="bc-badge bc-badge--amber" style={{ background: 'var(--amber-100)', color: 'var(--amber-800)' }}>Antecipada</span>}
            {t.note && <span>{t.note}</span>}
          </span>
        </div>
        <span className="bc-invoice-row__date">{t.date}</span>
        <span className="bc-invoice-row__amt">− R$ {fmt(t.amount)}</span>
        <div className="bc-tx__actions">
          {!isChild && (
            <>
              {onAmortize && t.installment_group_id && (t.installment_index ?? 0) < (t.installment_count ?? 0) && (
                <button
                  className="bc-tx__amortize"
                  aria-label="Adiantar parcelas"
                  onClick={() => onAmortize(t)}
                >
                  <FastForward size={14} />
                </button>
              )}
              {(!t.installment_group_id || t.installment_index === 1) && (
                <>
                  {onEdit && (
                    <button
                      className="bc-tx__edit"
                      aria-label="Editar lançamento"
                      onClick={() => {
                        onClose();
                        onEdit(t);
                      }}
                    >
                      <Pencil size={14} />
                    </button>
                  )}
                  {onDelete && (
                    <button
                      className="bc-tx__del"
                      aria-label="Excluir lançamento"
                      onClick={() => {
                        onClose();
                        (t.installment_group_id && onDeleteGroup) ? onDeleteGroup(t) : onDelete(t.id);
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    )
  }

  if (!open || !card) return null

  async function handleMarkAsPaid() {
    if (!onMarkAsPaid || unpaidCount === 0 || !cardId) return
    setMarking(true)
    try {
      await onMarkAsPaid(cardId)
    } finally {
      setMarking(false)
    }
  }

  return (
    <div className="bc-sheet-overlay" onClick={onClose}>
      <div className="bc-sheet bc-invoice-sheet" onClick={e => e.stopPropagation()}>
        <div className="bc-sheet__header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div className="bc-cc-mini" style={{ flexShrink: 0, background: card.tone }}>
              <span className="bc-cc-mini__last">•• {card.last4}</span>
            </div>
            <div>
              <h2 className="bc-sheet__title" style={{ marginBottom: 0 }}>Fatura {card.name}</h2>
              {card.due && (
                <span style={{ fontSize: '12px', color: 'var(--ink-4)' }}>Vence dia {card.due}</span>
              )}
            </div>
          </div>
          <button className="bc-iconbtn" onClick={onClose} aria-label="Fechar">
            <X size={18} />
          </button>
        </div>

        <div className="bc-sheet__body bc-invoice-sheet__body" style={{ padding: '0', gap: 0 }}>
          {items.length > 0 && (
            <div className="bc-invoice-search">
              <div className="bc-invoice-search__wrap">
                <Search size={15} className="bc-invoice-search__icon" />
                <input
                  className="bc-invoice-search__input"
                  type="text"
                  placeholder="Buscar por descrição ou valor..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  aria-label="Buscar transações da fatura por descrição ou valor"
                />
                {query && (
                  <button
                    className="bc-invoice-search__clear"
                    type="button"
                    onClick={() => setQuery('')}
                    aria-label="Limpar busca da fatura"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
          )}
          <div className="bc-invoice-results">
            {items.length === 0 ? (
              <p className="bc-empty bc-invoice-results__empty" style={{ padding: '24px' }}>
                Nenhuma despesa registrada neste cartão no mês atual.
              </p>
            ) : filteredItems.length === 0 ? (
              <p className="bc-empty bc-invoice-results__empty" style={{ padding: '24px' }}>
                Nenhuma transação encontrada para esta busca.
              </p>
            ) : (
              <div className="bc-list bc-invoice-list">
                {groupInstallments(filteredItems).map(item => {
                  if (item.type === 'transaction') return renderTransaction(item.transaction)

                  const expanded = expandedGroups.has(item.groupId)
                  return (
                    <div key={`group-${item.groupId}`}>
                      <div
                        className="bc-invoice-row bc-invoice-row--group"
                        onClick={() => toggleGroup(item.groupId)}
                        style={{ cursor: 'pointer', background: expanded ? 'var(--sand-1)' : undefined }}
                      >
                        <div className="bc-invoice-row__icon">
                          {CAT_ABBR[item.category] ?? item.category.slice(0, 2).toUpperCase()}
                        </div>
                        <div className="bc-invoice-row__desc">
                          <span className="bc-invoice-row__title">{item.title}</span>
                          <span className="bc-invoice-row__meta">
                            <span>{item.category}</span>
                            <span className="bc-badge bc-badge--amber" style={{ background: 'var(--amber-100)', color: 'var(--amber-800)' }}>
                              {item.count} parcelas antecipadas
                            </span>
                          </span>
                        </div>
                        <span className="bc-invoice-row__date">{item.date}</span>
                        <span className="bc-invoice-row__amt">− R$ {fmt(item.total)}</span>
                        <div className="bc-tx__actions" style={{ justifyContent: 'flex-end', opacity: 1 }}>
                          <button className="bc-iconbtn" aria-label="Expandir">
                            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                        </div>
                      </div>
                      {expanded && item.transactions.map(t => renderTransaction(t, true))}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        <div className="bc-sheet__footer" style={{ flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span style={{ fontSize: '13px', color: 'var(--ink-3)' }}>
              {isFiltering
                ? `${filteredItems.length} de ${items.length} ${items.length === 1 ? 'lançamento' : 'lançamentos'}`
                : `${items.length} ${items.length === 1 ? 'lançamento' : 'lançamentos'}`}
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
              <span style={{ fontSize: '11px', color: 'var(--ink-4)' }}>
                {isFiltering ? 'Total filtrado' : 'Total da fatura'}
              </span>
              <span style={{ font: '700 16px var(--font-mono)', color: 'var(--expense-700)', fontVariantNumeric: 'tabular-nums' }}>
                R$ {fmt(isFiltering ? filteredTotal : total)}
              </span>
            </div>
          </div>
          {onMarkAsPaid && items.length > 0 && (
            <button
              className="bc-btn bc-btn--primary"
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
              onClick={handleMarkAsPaid}
              disabled={unpaidCount === 0 || marking}
            >
              <CheckCircle2 size={15} />
              {marking
                ? 'Marcando...'
                : unpaidCount === 0
                  ? 'Fatura já paga'
                  : `Marcar fatura como paga (${unpaidCount})`}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
